// src/pages/PatientTriageReportPage.jsx
// Route: /patient/results/:triageId/report
//
// The full clinical report as its own page for the patient portal (it used to
// open as a modal over the results screen). Same report component, read-only.
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import TriageReportModal from '../components/healthrecords/TriageReportModal';
import { loadPatientReport } from '../utils/patientReportData';

export default function PatientTriageReportPage() {
  const { triageId } = useParams();
  const navigate = useNavigate();
  const { patient } = usePatient();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!patient?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await loadPatientReport(patient.id, triageId);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setError('Could not load the full report. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [patient?.id, triageId]);

  const back = () => navigate(`/patient/results/${triageId}`);

  return (
    <PatientAppShell breadcrumbs={[
      { label: 'My Results', to: '/patient/results' },
      { label: 'Full report' },
    ]}>
      <div style={{ padding: '12px 0 60px' }}>
        <span className="back-link" onClick={back}>← Back to my results</span>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        {!data && !error && (
          <div className="loading-state" style={{ padding: 40 }}>
            <div className="dot-loader"><span /><span /><span /></div>
            <div style={{ fontSize: 13 }}>Loading report…</div>
          </div>
        )}
        {data && (
          <TriageReportModal
            asPage
            readOnly
            triageResult={data.result}
            healthRecord={data.record}
            patient={patient}
            triageHistory={data.history}
            onClose={back}
          />
        )}
      </div>
    </PatientAppShell>
  );
}
